import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.#setupBonbodiGiftPicker();

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')
        ?.shadowRoot
        ?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal =
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() =>
            this.#updateStickyState()
          );

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #setupBonbodiGiftPicker() {
    if (window.__bonbodiGiftPickerInitialised) {
      return;
    }

    window.__bonbodiGiftPickerInitialised = true;

    document.addEventListener('click', async (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const button = target.closest(
        '[data-gift-variant]'
      );

      if (!button) {
        return;
      }

      const picker = button.closest(
        '[data-bonbodi-gift-picker]'
      );

      if (!picker) {
        return;
      }

      event.preventDefault();

      const variantId =
        button.dataset.giftVariant;

      if (!variantId || variantId === '0') {
        console.error(
          '[bonbodi-gift] No gift variant ID found.'
        );

        const status =
          picker.querySelector(
            '[data-gift-status]'
          );

        if (status) {
          status.textContent =
            'GIFT TEMPORARILY UNAVAILABLE';
        }

        return;
      }

      const buttons =
        picker.querySelectorAll(
          '[data-gift-variant]'
        );

      const status =
        picker.querySelector(
          '[data-gift-status]'
        );

      buttons.forEach((item) => {
        item.disabled = true;
      });

      button.classList.add('is-selected');

      if (status) {
        status.textContent =
          'ADDING YOUR GIFT...';
      }

      try {
        /*
         * Get current cart.
         */
        const cartResponse = await fetch(
          `${window.Shopify.routes.root}cart.js`,
          {
            headers: {
              Accept: 'application/json'
            }
          }
        );

        if (!cartResponse.ok) {
          throw new Error(
            'Could not read cart'
          );
        }

        const cart =
          await cartResponse.json();

        /*
         * Remove an existing Bonbodi gift first.
         * This guarantees only one gift.
         */
        const existingGifts =
          cart.items.filter(
            (item) =>
              item.product_id === 9705833693335 ||
              item.product_id === 9705834021015
          );

        for (const gift of existingGifts) {
          const removeResponse =
            await fetch(
              `${window.Shopify.routes.root}cart/change.js`,
              {
                method: 'POST',
                headers: {
                  'Content-Type':
                    'application/json',
                  Accept:
                    'application/json'
                },
                body: JSON.stringify({
                  id: gift.key,
                  quantity: 0
                })
              }
            );

          if (!removeResponse.ok) {
            throw new Error(
              'Could not remove existing gift'
            );
          }
        }

        /*
         * Add the selected $0 gift.
         */
        const addResponse =
          await fetch(
            `${window.Shopify.routes.root}cart/add.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type':
                  'application/json',
                Accept:
                  'application/json'
              },
              body: JSON.stringify({
                items: [
                  {
                    id: Number(variantId),
                    quantity: 1
                  }
                ]
              })
            }
          );

        if (!addResponse.ok) {
          const errorText =
            await addResponse.text();

          console.error(
            '[bonbodi-gift] Add failed:',
            errorText
          );

          throw new Error(
            'Gift could not be added'
          );
        }

        /*
         * Refresh the ACTUAL Pitch cart drawer section.
         */
        const sectionsResponse =
          await fetch(
            `${window.Shopify.routes.root}?sections=cart-drawer-section`,
            {
              headers: {
                Accept:
                  'application/json'
              }
            }
          );

        if (!sectionsResponse.ok) {
          throw new Error(
            'Could not refresh cart drawer'
          );
        }

        const sections =
          await sectionsResponse.json();

        const drawerMarkup =
          sections[
            'cart-drawer-section'
          ];

        if (!drawerMarkup) {
          throw new Error(
            'Cart drawer section not returned'
          );
        }

        /*
         * Find the current drawer wrapper.
         */
        const parser =
          new DOMParser();

        const documentFragment =
          parser.parseFromString(
            drawerMarkup,
            'text/html'
          );

        const newDrawer =
          documentFragment.querySelector(
            'theme-drawer'
          );

        const currentDrawer =
          document.querySelector(
            'theme-drawer#cart-drawer'
          );

        if (
          newDrawer &&
          currentDrawer
        ) {
          currentDrawer.replaceWith(
            newDrawer
          );
        }

        /*
         * Re-open the drawer after the
         * section has been replaced.
         */
        const updatedDrawer =
          document.querySelector(
            'theme-drawer#cart-drawer'
          );

        if (updatedDrawer) {
          requestAnimationFrame(() => {
            updatedDrawer.open();
          });
        }

      } catch (error) {
        console.error(
          '[bonbodi-gift]',
          error
        );

        buttons.forEach((item) => {
          item.disabled = false;
          item.classList.remove(
            'is-selected'
          );
        });

        if (status) {
          status.textContent =
            'PLEASE TRY AGAIN';
        }
      }
    });
  }

  #isCartEmpty() {
    return Boolean(
      this.querySelector(
        '.cart-drawer--empty'
      )
    );
  }

  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) {
      return;
    }

    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}